/**
 * The "you have completed your hours" reminder, from the app's side.
 *
 * Only the Android app can do this: it hands the phone an alarm for the moment the hours will be
 * complete, and the phone asks the server before showing anything, so a punch-out from another
 * device or the day's auto-close leaves it silent. A browser or an iPhone has no such alarm, so
 * here it does nothing at all, and nothing they had is changed.
 *
 * The server says *when*: `reminder_at` on the punch status is the instant the target is met if
 * the worker keeps going, or null when there is nothing to wait for — no open shift, hours
 * already done, or a day that will close first. This module only relays that to the phone, once
 * per change, so that whichever screen loaded the status — after a punch, on a poll, on opening
 * the app after punching in from a browser — the phone ends up holding the right alarm.
 */
import { API_URL } from './api.js';
import { isNativeApp, nativeCancelShiftReminder, nativeScheduleShiftReminder } from './native.js';
import { t } from '../i18n/index.js';

export type ReminderStatus = {
  server_time: string;
  work_date: string;
  open_shift: unknown;
  reminder_at: string | null;
  target_minutes: number;
};

export type ReminderPlan =
  | {
      kind: 'schedule';
      /**
       * How far away the moment is, by the server's clock — not the moment itself. The phone
       * sets its alarm that far into its own uptime, so a phone whose clock is a day out (a
       * worker tried exactly that) still hears from it at the right time.
       */
      in_ms: number;
      work_date: string;
      api_base: string;
      title: string;
      body: string;
    }
  | { kind: 'cancel' };

/** "9 hours", "5 hours", "7 hours 30 minutes". */
export function hoursPhrase(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = h === 1 ? '1 hour' : `${h} hours`;
  if (m === 0) return hours;
  return `${hours} ${m} ${m === 1 ? 'minute' : 'minutes'}`;
}

/** What the phone should hold after this status: an alarm at a moment, or nothing. */
export function planFor(status: ReminderStatus, apiBase: string = API_URL): ReminderPlan {
  if (!status.open_shift || !status.reminder_at) return { kind: 'cancel' };
  const inMs = Date.parse(status.reminder_at) - Date.parse(status.server_time);
  if (!Number.isFinite(inMs) || inMs <= 0) return { kind: 'cancel' };
  return {
    kind: 'schedule',
    in_ms: inMs,
    work_date: status.work_date,
    api_base: apiBase,
    title: t('shift_done_title'),
    body: t('shift_done_body', { hours: hoursPhrase(status.target_minutes) }),
  };
}

type Bridge = {
  schedule: (plan: Extract<ReminderPlan, { kind: 'schedule' }>) => Promise<void>;
  cancel: () => Promise<void>;
};

const nativeBridge: Bridge = {
  schedule: nativeScheduleShiftReminder,
  cancel: nativeCancelShiftReminder,
};

/**
 * The status is polled every thirty seconds, and the moment it names drifts by up to a minute
 * between polls because worked minutes are floored. The phone is told once per real change —
 * a different day, or a moment more than a minute away from the last one — not on every poll.
 * "The moment" is kept on the monotonic clock (uptime plus the delay), which is the one thing
 * here a worker cannot move.
 */
let last: { work_date: string; at: number } | null | undefined;

/** Uptime, not wall-clock: unaffected by a phone whose date has been changed. */
const mono = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

/** Forget what was last sent, so a test (or a sign-out) starts clean. */
export function resetShiftReminder(): void {
  last = undefined;
}

const sameAlarm = (a: { work_date: string; at: number }, b: { work_date: string; at: number }) =>
  a.work_date === b.work_date && Math.abs(a.at - b.at) <= 60_000;

/**
 * Bring the phone's alarm in line with this status. Native app only; a no-op everywhere else.
 * Failures are swallowed: a reminder that could not be set must never get in the way of the
 * punch that was just made.
 */
export async function syncShiftReminder(
  status: ReminderStatus,
  bridge: Bridge = nativeBridge,
  native: boolean = isNativeApp(),
): Promise<void> {
  if (!native) return;
  const plan = planFor(status);
  try {
    if (plan.kind === 'cancel') {
      if (last === null) return;
      last = null;
      await bridge.cancel();
      return;
    }
    const due = { work_date: plan.work_date, at: mono() + plan.in_ms };
    if (last && sameAlarm(last, due)) return;
    last = due;
    await bridge.schedule(plan);
  } catch {
    // The phone did not take it; the next status load tries again.
    last = undefined;
  }
}
